import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
    BrowserRouter,
    Route,
    Switch,
    Redirect,
    useHistory,
    useLocation,
    useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import { initialLanguage, supportedLanguages } from './i18n';

// Pages
import HomePage from './pages/Home.jsx';
import PostsPage from './pages/Posts.jsx';
import FeaturedPostsPage from './pages/FeaturedPosts.jsx';
import AboutPage from './pages/About.jsx';
import AuthorProfile from './pages/AuthorProfile.jsx';

import BlogPost from './components/BlogPost/BlogPost';

import * as serviceWorker from './serviceWorker';
import { AnimatePresence } from 'framer-motion';
import { GlobalStyle } from './components/StyledComponents/StyledComponents.jsx';
import { ThemeContextProvider } from './contexts/ThemeContext';

import Posts from '../src/posts/Posts';

// Google Analytics
import ReactGA from 'react-ga';

const AnalyticsTracker = () => {
    const location = useLocation();
    const gaInitialized = useRef(false);

    useEffect(() => {
        if (window.location.hostname === 'localhost') {
            return;
        }

        const trackingId = 'G-NEWDFQ2BZ5';
        if (!gaInitialized.current) {
            ReactGA.initialize(trackingId);
            gaInitialized.current = true;
        }

        ReactGA.pageview(location.pathname + location.search);
    }, [location]);

    return null;
};

const LanguageRoutes = () => {
    const { i18n } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const { lang } = useParams();

    const normalizedLang = supportedLanguages.includes(lang)
        ? lang
        : initialLanguage;

    useEffect(() => {
        // Redirect to normalized language if URL has unsupported language
        if (lang !== normalizedLang) {
            const pathSegments = location.pathname
                .split('/')
                .filter((segment) => segment);
            const hasValidLang = supportedLanguages.includes(lang);
            const tail = hasValidLang ? pathSegments.slice(1) : pathSegments; // preserve entire path (e.g., /posts/...) when no lang
            const pathWithoutLang = tail.join('/');
            const newPath = `/${normalizedLang}${
                pathWithoutLang ? `/${pathWithoutLang}` : ''
            }${location.search}`;

            history.replace(newPath);
            return;
        }

        // Sync i18n language with URL language
        if (i18n.language !== normalizedLang) {
            i18n.changeLanguage(normalizedLang);
        }
    }, [
        lang,
        normalizedLang,
        history,
        i18n,
        location.pathname,
        location.search,
    ]);

    return (
        <>
            <AnalyticsTracker />
            <Switch>
                <Route exact path="/:lang">
                    <HomePage />
                </Route>

                <Route exact path="/:lang/posts">
                    <PostsPage />
                </Route>

                <Route path="/:lang/featured-posts">
                    <FeaturedPostsPage />
                </Route>

                <Route path="/:lang/about">
                    <AboutPage />
                </Route>

                <Route path="/:lang/guias/:authorId">
                    <AuthorProfile />
                </Route>

                {Posts.map((post, index) => {
                    return (
                        <Route key={index} path={`/:lang/posts/${post.route}`}>
                            <BlogPost />
                        </Route>
                    );
                })}

                <Redirect to={`/${normalizedLang}`} />
            </Switch>
        </>
    );
};

const App = () => {
    return (
        <ThemeContextProvider>
            <GlobalStyle />
            <BrowserRouter basename="/blog">
                <AnimatePresence>
                    <Switch>
                        <Route exact path="/">
                            <Redirect to={`/${initialLanguage}`} />
                        </Route>
                        <Route path="/:lang">
                            <LanguageRoutes />
                        </Route>
                        <Redirect to={`/${initialLanguage}`} />
                    </Switch>
                </AnimatePresence>
            </BrowserRouter>
        </ThemeContextProvider>
    );
};

serviceWorker.unregister();
ReactDOM.render(<App />, document.getElementById('root'));
